import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import type { FocusEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SubmitHandler, UseFormSetValue } from 'react-hook-form';

import { getLineItemsApi } from 'api/repairOrder';
import { getAdvisorNumberListApi, getTechNumberListApi } from 'api/tickets';
import { useTickets } from 'contexts/TicketsContext';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { getOemFamilyIdByRoofTopId } from 'helpers/getOemFamilyByRoofTop';
import { convertNumbersToSelectOptions } from 'helpers/selectOptions';
import { useFetchClaimStatuses } from 'hooks/api/useClaimStatuses';
import { useFetchTicketPriorities } from 'hooks/api/useTicketPriorities';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import { useFetchTicketRoExists } from 'hooks/api/useTicketRoNumber';
import { useChangeTicketDetails } from 'hooks/api/useTickets';
import {
  useFetchTicketRequestSubcategories,
  useFetchTicketRequestSubcategoriesByCategoryId,
} from 'hooks/api/useTicketSubcategories';

import useTicketDetailsPopup from './useTicketDetailsPopup';

export type ChangeTicketDetailsFormValuesT = {
  ticketId: string;
  ticketRoofTopId: string;
  amount: string;
  claimStatusId: number;
  claimSubmittedDate: Date | null;
  advisorNumber: string;
  techNumber: string;
  ticketPriorityId: number;
  subCategoryId: number | null;
  dmsScheduleAccountNumber: string | null;
};

type UseChangeTicketPriorityPopupT = {
  ticketPrioritiesOptions: SelectOptionT[];
  claimStatusesOptions: SelectOptionT[];
  ticketSubcategoriesOptions: SelectOptionT[];
  isLoadingTicketPrioritiesOptions: boolean;
  isLoadingClaimStatusesOptions: boolean;
  isLoadingTicketSubcategoriesOptions: boolean;
  isChangingTicketDetails: boolean;
  isTicketDetailsChanged: boolean;
  isTicketDetailsChangeError: boolean;
  changeTicketDetailsErrorMessage: string | undefined;
  changeTicketDetailsValidationErrorMessage: string | undefined;
  showPopupContent: boolean;
  handleAmountBlur: (
    setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>
  ) => (e: FocusEvent<HTMLInputElement>) => void;
  handleChangeTicketDetails: SubmitHandler<ChangeTicketDetailsFormValuesT>;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
  advisorNumbers: SelectOptionT[];
  techNumbers: SelectOptionT[];
  showAdvisorNumberDropdown: boolean;
  showTechNumberDropdown: boolean;
  isLoadingAdvisorNumbers: boolean;
  isLoadingTechNumbers: boolean;
  initializeRoCheck: (setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>) => void;
};

const useChangeTicketDetailsForm = (): UseChangeTicketPriorityPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const { ticketDetails, selectedTicketId } = useTicketDetailsPopup();
  const { roofTopsList, activeTab, selectedRoofTop } = useTickets();
  const roofTopId = ticketDetails?.roofTop?.id ?? (selectedRoofTop || undefined);
  const oemFamilyId = useMemo(
    (): string | undefined => getOemFamilyIdByRoofTopId(roofTopId, roofTopsList),
    [roofTopId, roofTopsList]
  );
  const [advisorNumbers, setAdvisorNumbers] = useState<SelectOptionT[]>([]);
  const [techNumbers, setTechNumbers] = useState<SelectOptionT[]>([]);
  const [showAdvisorNumberDropdown, setShowAdvisorNumberDropdown] = useState(false);
  const [showTechNumberDropdown, setShowTechNumberDropdown] = useState(false);
  const [isLoadingAdvisorNumbers, setIsLoadingAdvisorNumbers] = useState(false);
  const [isLoadingTechNumbers, setIsLoadingTechNumbers] = useState(false);
  const [advisorNumber, setAdvisorNumber] = useState<string | undefined>(undefined);
  const [recentRoData, setRecentRoData] = useState<{
    roofTopId: string | null;
    roNumber: string | null;
  }>({
    roofTopId: null,
    roNumber: null,
  });
  const initializedTicketIdRef = useRef<string | null>(null);

  const claimStatusesRequestParams = {
    isEnabled: true,
    roofTopId: ticketDetails?.roofTop.id || '',
  };

  // Fetch categories to get categoryId from categoryName
  const ticketRequestCategoriesResult = useFetchTicketRequestCategories(
    !!ticketDetails,
    ticketDetails?.roofTop.id
  );

  // Get categoryId from categoryName
  const selectedCategoryId = useMemo(() => {
    if (!ticketDetails?.categoryName || !ticketRequestCategoriesResult.data) {
      return undefined;
    }
    const category = ticketRequestCategoriesResult.data.find(
      cat => cat.categoryName.toLowerCase() === ticketDetails.categoryName.toLowerCase()
    );
    return category?.id;
  }, [ticketDetails?.categoryName, ticketRequestCategoriesResult.data]);

  // Determine if this is a schedule ticket (has subCategoryId) or regular ticket (has requestSubCategoryId)
  // When in Schedule section list view (ticketDetails is null), treat as schedule for subcategory options
  const isScheduleTicket = useMemo(() => {
    return (
      activeTab === ActiveTabTypeE.Schedule ||
      ticketDetails?.categoryName?.toLowerCase() === 'schedule' ||
      !!ticketDetails?.subCategoryId
    );
  }, [activeTab, ticketDetails?.categoryName, ticketDetails?.subCategoryId]);

  // Fetch old subcategories API for schedule tickets (list view or ticket detail)
  const oldTicketSubcategoriesResult = useFetchTicketRequestSubcategories({
    isEnabled: isScheduleTicket && !!roofTopId,
    roofTopId: roofTopId || '',
    oemFamilyId,
  });

  // Reverted: subcategories for non-schedule tickets (only schedule uses subcategories)
  const ticketSubcategoriesResult = useFetchTicketRequestSubcategoriesByCategoryId({
    isEnabled: false,
    categoryId: selectedCategoryId,
    oemFamilyId,
  });

  /**
   * Api hooks
   */
  const ticketPrioritiesResult = useFetchTicketPriorities(true);

  const claimStatusesResult = useFetchClaimStatuses(claimStatusesRequestParams);

  const roQuery = useFetchTicketRoExists(
    recentRoData.roofTopId as string,
    recentRoData.roNumber as string
  );

  const changeTicketDetailsMutation = useChangeTicketDetails({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketsCountByRoofTop]);
    },
  });

  /**
   * Variables
   */
  const ticketPrioritiesOptions = useMemo(
    () =>
      ticketPrioritiesResult.data
        ? ticketPrioritiesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [ticketPrioritiesResult.data]
  );

  // Use old API options for schedule tickets, new API options for regular tickets
  const ticketSubcategoriesOptions = useMemo(() => {
    if (isScheduleTicket && oldTicketSubcategoriesResult.data) {
      return oldTicketSubcategoriesResult.data.map(({ id, name }) => ({
        label: name,
        value: id,
      }));
    }
    if (!isScheduleTicket && ticketSubcategoriesResult.data) {
      return ticketSubcategoriesResult.data.map(({ id, name }) => ({
        label: name,
        value: id,
      }));
    }
    return [];
  }, [isScheduleTicket, oldTicketSubcategoriesResult.data, ticketSubcategoriesResult.data]);

  const changeTicketDetailsErrorMessage = useMemo(
    () => getErrorMessage(changeTicketDetailsMutation.error),
    [changeTicketDetailsMutation.error]
  );

  const changeTicketDetailsValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(changeTicketDetailsMutation.error),
    [changeTicketDetailsMutation.error]
  );

  const claimStatusesOptions = useMemo(
    () =>
      claimStatusesResult.data
        ? claimStatusesResult.data?.map(({ id, name }) => ({
            label: name,
            value: id,
          }))
        : [],
    [claimStatusesResult.data]
  );

  /**
   * Handlers
   */
  const resetChangeTicketDetailsMutationState = useCallback(() => {
    if (changeTicketDetailsMutation.isSuccess || changeTicketDetailsMutation.isError) {
      changeTicketDetailsMutation.reset();
    }
  }, [changeTicketDetailsMutation]);

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetChangeTicketDetailsMutationState();
    },
    [resetChangeTicketDetailsMutationState]
  );

  const handleChangeTicketDetails = useCallback(
    (data: ChangeTicketDetailsFormValuesT) => {
      const isScheduleTicket = ticketDetails?.categoryName.toLowerCase() === 'schedule';

      changeTicketDetailsMutation.mutate({
        ...data,
        claimSubmittedDate: data.claimSubmittedDate
          ? format(data.claimSubmittedDate, 'yyyy-MM-dd')
          : null,
        subCategoryId: isScheduleTicket && data.subCategoryId ? data.subCategoryId : null,
        requestSubCategoryId: null,
        requestSubCategoryName: null,
      });
    },
    [changeTicketDetailsMutation, ticketDetails?.categoryName]
  );

  const setAmountValue = (
    value: string,
    setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>
  ): void => {
    setValue('amount', value, { shouldValidate: true });
  };

  const handleAmountBlur = useCallback(
    (setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>) =>
      (e: FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const [numbersBeforeDot, numbersAfterDot] = value.split('.');
        const hasOneDecimal = !!numbersAfterDot && numbersAfterDot.length === 1;
        const hasTwoDecimal = !!numbersAfterDot && numbersAfterDot.length >= 2;

        if (!value) {
          setAmountValue('0.00', setValue);
        } else if (value && hasOneDecimal) {
          setAmountValue(`${numbersBeforeDot}.${numbersAfterDot}0`, setValue);
        } else if (value && !hasTwoDecimal) {
          setAmountValue(`${numbersBeforeDot}.00`, setValue);
        }
      },
    []
  );

  const setAdvisorLineNumber = async (
    roofTopId: string,
    roNumber: string,
    setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>
  ): Promise<void> => {
    setRecentRoData(prevRoData => ({
      ...prevRoData,
      roofTopId: roofTopId,
      roNumber: roNumber,
    }));
    const roResult = roQuery.data;
    // Prioritize ticketDetails value over roResult, only use roResult if ticketDetails doesn't have it
    const currentAdvisorNumber =
      ticketDetails?.advisorNumber ?? roResult?.serviceAdvisorNumber ?? advisorNumber;
    if (currentAdvisorNumber) {
      setValue('advisorNumber', currentAdvisorNumber);
      setAdvisorNumber(currentAdvisorNumber);
    }
    if (roQuery.isError || (roNumber && roQuery.data === undefined)) {
      setIsLoadingAdvisorNumbers(true);
      setIsLoadingTechNumbers(true);

      let advisorOptions: SelectOptionT[] = [];
      let techOptions: SelectOptionT[] = [];
      try {
        const advisorNumbersApi = await getAdvisorNumberListApi(roofTopId);
        advisorOptions = convertNumbersToSelectOptions(advisorNumbersApi);
      } catch (e) {
        // If fetching advisor numbers fails (e.g., 400 when RoofTopOEM missing), fallback to manual input
        advisorOptions = [];
      } finally {
        setIsLoadingAdvisorNumbers(false);
      }

      try {
        const techNumbersApi = await getTechNumberListApi(roofTopId);
        techOptions = convertNumbersToSelectOptions(techNumbersApi);
      } catch (e) {
        // If fetching tech numbers fails (e.g., 400 when RoofTopOEM missing), fallback to manual input
        techOptions = [];
      } finally {
        setIsLoadingTechNumbers(false);
      }

      // Merge with existing options (from ticketDetails) and add fetched options
      setAdvisorNumbers(prev => {
        const merged = [...prev];
        advisorOptions.forEach(opt => {
          if (!merged.find(existing => existing.value === opt.value)) {
            merged.push(opt);
          }
        });
        // Ensure ticketDetails value is always included with proper label
        if (ticketDetails?.advisorNumber) {
          const existingIndex = merged.findIndex(opt => opt.value === ticketDetails.advisorNumber);
          if (existingIndex === -1) {
            // Value not found, add it with just the number as label
            merged.push({ value: ticketDetails.advisorNumber, label: ticketDetails.advisorNumber });
          } else {
            // Value found, ensure it has the full label (number - extraString1) if available
            const apiOption = advisorOptions.find(opt => opt.value === ticketDetails.advisorNumber);
            if (apiOption) {
              merged[existingIndex] = apiOption;
            }
          }
        }
        return merged;
      });

      setTechNumbers(prev => {
        const merged = [...prev];
        techOptions.forEach(opt => {
          if (!merged.find(existing => existing.value === opt.value)) {
            merged.push(opt);
          }
        });
        // Ensure ticketDetails value is always included with proper label
        if (ticketDetails?.techNumber) {
          const existingIndex = merged.findIndex(opt => opt.value === ticketDetails.techNumber);
          if (existingIndex === -1) {
            // Value not found, add it with just the number as label
            merged.push({ value: ticketDetails.techNumber, label: ticketDetails.techNumber });
          } else {
            // Value found, ensure it has the full label (number - extraString1) if available
            const apiOption = techOptions.find(opt => opt.value === ticketDetails.techNumber);
            if (apiOption) {
              merged[existingIndex] = apiOption;
            }
          }
        }
        return merged;
      });

      // Set dropdown visibility based on whether we have API options (not just ticketDetails values)
      const hasAdvisorOptions = advisorOptions.length > 0;
      const hasTechOptions = techOptions.length > 0;
      setShowAdvisorNumberDropdown(hasAdvisorOptions);
      setShowTechNumberDropdown(hasTechOptions);
    } else {
      const lineItems = getLineItemsApi(roofTopId, roNumber);
      await lineItems;
      // Even if RO exists, ensure existing values from ticketDetails are preserved
      // Set advisorNumber from ticketDetails if it exists
      if (ticketDetails?.advisorNumber) {
        setValue('advisorNumber', ticketDetails.advisorNumber);
        setAdvisorNumber(ticketDetails.advisorNumber);
      }
      // Ensure existing values from ticketDetails are preserved in options
      setAdvisorNumbers(prev => {
        if (
          ticketDetails?.advisorNumber &&
          !prev.find(opt => opt.value === ticketDetails.advisorNumber)
        ) {
          return [
            ...prev,
            { value: ticketDetails.advisorNumber, label: ticketDetails.advisorNumber },
          ];
        }
        return prev;
      });
      setTechNumbers(prev => {
        if (
          ticketDetails?.techNumber &&
          !prev.find(opt => opt.value === ticketDetails.techNumber)
        ) {
          return [...prev, { value: ticketDetails.techNumber, label: ticketDetails.techNumber }];
        }
        return prev;
      });
      // When RO exists, hide dropdowns (use regular input)
      setShowAdvisorNumberDropdown(false);
      setShowTechNumberDropdown(false);
    }
  };

  // Reset ref when popup closes (selectedTicketId becomes null)
  useEffect(() => {
    if (!selectedTicketId) {
      initializedTicketIdRef.current = null;
      // Clear state when popup closes
      setRecentRoData({ roofTopId: null, roNumber: null });
      setAdvisorNumbers([]);
      setTechNumbers([]);
      setShowAdvisorNumberDropdown(false);
      setShowTechNumberDropdown(false);
      setIsLoadingAdvisorNumbers(false);
      setIsLoadingTechNumbers(false);
    }
  }, [selectedTicketId]);

  // Check RO when ticket details are loaded
  const initializeRoCheck = useCallback(
    (setValue: UseFormSetValue<ChangeTicketDetailsFormValuesT>) => {
      if (!ticketDetails?.id) {
        return;
      }

      // Reset ref if ticket ID changed (new ticket or ticket updated after save)
      // Also reset if ref is null (popup was closed and reopened)
      if (initializedTicketIdRef.current !== ticketDetails.id) {
        initializedTicketIdRef.current = ticketDetails.id;
        // Clear previous options when switching tickets
        setAdvisorNumbers([]);
        setTechNumbers([]);
        setShowAdvisorNumberDropdown(false);
        setShowTechNumberDropdown(false);
      }

      if (ticketDetails?.roofTop?.id && ticketDetails?.roNumber) {
        // First, add existing advisor/tech numbers to options immediately so they're visible
        const initialAdvisorOptions: SelectOptionT[] = [];
        const initialTechOptions: SelectOptionT[] = [];

        if (ticketDetails.advisorNumber) {
          initialAdvisorOptions.push({
            value: ticketDetails.advisorNumber,
            label: ticketDetails.advisorNumber,
          });
        }
        if (ticketDetails.techNumber) {
          initialTechOptions.push({
            value: ticketDetails.techNumber,
            label: ticketDetails.techNumber,
          });
        }

        setAdvisorNumbers(initialAdvisorOptions);
        setTechNumbers(initialTechOptions);

        // Then check if RO exists and fetch advisor/tech numbers to merge with existing
        const roofTopId = ticketDetails.roofTop.id;
        const roNumber = ticketDetails.roNumber;
        if (roofTopId && roNumber) {
          setAdvisorLineNumber(roofTopId, roNumber, setValue);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ticketDetails]
  );

  return {
    isLoadingTicketPrioritiesOptions: ticketPrioritiesResult.isLoading,
    isLoadingClaimStatusesOptions: claimStatusesResult.isLoading,
    isLoadingTicketSubcategoriesOptions: isScheduleTicket
      ? oldTicketSubcategoriesResult.isLoading
      : ticketSubcategoriesResult.isLoading,
    ticketPrioritiesOptions,
    claimStatusesOptions,
    ticketSubcategoriesOptions,
    isChangingTicketDetails: changeTicketDetailsMutation.isLoading,
    isTicketDetailsChanged: changeTicketDetailsMutation.isSuccess,
    isTicketDetailsChangeError: changeTicketDetailsMutation.isError,
    changeTicketDetailsErrorMessage,
    changeTicketDetailsValidationErrorMessage,
    showPopupContent: changeTicketDetailsMutation.isIdle || changeTicketDetailsMutation.isLoading,
    handleChangeTicketDetails,
    handleClosePopup,
    handleAmountBlur,
    advisorNumbers,
    techNumbers,
    showAdvisorNumberDropdown,
    showTechNumberDropdown,
    isLoadingAdvisorNumbers,
    isLoadingTechNumbers,
    initializeRoCheck,
  };
};

export default useChangeTicketDetailsForm;
